import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import DocumentSearchList from '../../../../components/Documents/DocumentSearchList';
import DocumentUpload from '../../../../components/Documents/DocumentUpload';
import { useAuth } from '../../../../contexts/AuthContext';
import dealService from '../../../../service/dealService.ts';

const DOCUMENT_TYPES = [
  { value: 'paymentVoucher', label: 'Voucher Documents', usePublicId: true },
  { value: 'blank_check', label: 'Blank Check Documents', usePublicId: false },
  { value: 'othersDocuments', label: 'Other Documents', usePublicId: true },
];

const DocumentsAction = ({ isOpen, toggle, deal }) => {
  const { getToken } = useAuth();
  const [showUpload, setShowUpload] = useState(false);
  const [selectedOriginType, setSelectedOriginType] = useState('paymentVoucher');
  const [refreshKey, setRefreshKey] = useState(0);
  const [blankCheckDocuments, setBlankCheckDocuments] = useState([]); // solo blank checks

  const getOriginId = (usePublicId) => {
    return usePublicId ? deal?.publicId : deal?.privateId;
  };

  const selectedType = DOCUMENT_TYPES.find(t => t.value === selectedOriginType) || DOCUMENT_TYPES[0];

  // 🔹 Cuando se sube un documento tipo blank_check
  const handleDocumentUploaded = async () => {
    try {
      if (selectedOriginType === 'blank_check' && deal?.privateId) {
        const token = getToken();
        if (token) {
          console.log('🔄 Updating has-blank-check to true...');
          await dealService.updateHasBlankCheck(token, deal.privateId, true);
          console.log('✅ has-blank-check updated successfully');
        }
      }
      setShowUpload(false);
      setRefreshKey(k => k + 1);
    } catch (error) {
      console.error('❌ Error updating has-blank-check:', error);
      setShowUpload(false);
      setRefreshKey(k => k + 1);
    }
  };

  // 🔹 Cuando se elimina un documento
  const handleDocumentDeleted = async (deletedDocument) => {
    try {
      if (deletedDocument?.originType === 'blank_check' && deal?.privateId) {
        const token = getToken();
        if (token) {
          console.log('🔍 Checking remaining blank checks...');
          // Actualizamos la lista local de blank checks
          setBlankCheckDocuments(prev => prev.filter(doc => doc.id !== deletedDocument.id));
          
          // Verificamos si todavía hay blank checks
          const remaining = blankCheckDocuments.filter(doc => doc.id !== deletedDocument.id);
          if (remaining.length === 0) {
            console.log('⚠️ No remaining blank checks. Updating has-blank-check to false...');
            await dealService.updateHasBlankCheck(token, deal.privateId, false);
            console.log('✅ has-blank-check updated successfully to false');
          } else {
            console.log('✅ Other blank checks still exist. Skipping update.');
          }
        }
      }
      setRefreshKey(k => k + 1);
    } catch (error) {
      console.error('❌ Error updating has-blank-check after delete:', error);
      setRefreshKey(k => k + 1);
    }
  };

  // 🔹 Sincroniza documentos de blank check
  const handleBlankCheckDocumentsLoaded = (docs) => {
    setBlankCheckDocuments(prev => {
      const merged = [...prev.filter(d => !docs.some(nd => nd.id === d.id)), ...docs];
      return merged;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      style={{ maxWidth: '1200px' }}
    >
      <ModalHeader toggle={toggle} style={{ padding: '1rem 1.5rem' }}>
        <i className="ni ni-folder-17 mr-2" />
        Deal Documents
      </ModalHeader>

      <ModalBody style={{ padding: '1rem 1.5rem', maxHeight: '80vh', overflowY: 'auto' }}>
        {/* Upload Section */}
        <Card className="shadow-sm mb-3">
          <CardHeader className="d-flex justify-content-between align-items-center" style={{ padding: '0.5rem 1rem' }}>
            <h6 className="mb-0 text-muted" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
              <i className="ni ni-cloud-upload-96 mr-2" />
              Upload Documents
            </h6>
            <Button color="primary" size="sm" onClick={() => setShowUpload(!showUpload)}>
              <i className={`ni ni-${showUpload ? 'fat-remove' : 'cloud-upload-96'} mr-1`} />
              {showUpload ? 'Hide' : 'Upload'}
            </Button>
          </CardHeader>

          {showUpload && (
            <CardBody style={{ padding: '0.75rem 1rem' }}>
              <div className="mb-3">
                <FormGroup className="mb-2">
                  <Label for="documentTypeSelect" style={{ fontWeight: '600', fontSize: '0.8rem' }}>
                    Document Type *
                  </Label>
                  <Input
                    type="select"
                    id="documentTypeSelect"
                    value={selectedOriginType}
                    onChange={(e) => setSelectedOriginType(e.target.value)}
                    bsSize="sm"
                    style={{ maxWidth: '300px' }}
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>

              <div
                style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '8px',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  textAlign: 'center',
                }}
              >
                <DocumentUpload
                  originId={getOriginId(selectedType.usePublicId)}
                  originType={selectedOriginType}
                  category="deal"
                  onUploaded={handleDocumentUploaded}
                  className="border-0 shadow-none"
                />
              </div>
            </CardBody>
          )}
        </Card>

        {/* Documents Grid */}
        <div className="row">
          {/* Left Column */}
          <div className="col-md-6">
            {/* Contract Documents */}
            <div className="mb-3">
              <h6 className="text-muted mb-2" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                <i className="ni ni-paper-diploma mr-1" />
                Contract Documents
              </h6>
               <DocumentSearchList
                 originId={deal?.publicId || deal?.privateId}
                 originType="deal"
                 pageSize={5}
                 refreshKey={refreshKey}
                 showDeleteButton={false}
               />
            </div>

            {/* Voucher Documents */}
            <div className="mb-3">
              <h6 className="text-muted mb-2" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                <i className="ni ni-credit-card mr-1" />
                Voucher Documents
              </h6>
               <DocumentSearchList
                 originId={deal?.publicId}
                 originType="paymentVoucher"
                 pageSize={5}
                 refreshKey={refreshKey}
                 showDeleteButton={true}
               />
            </div>

            {/* Funding Voucher */}
            <div className="mb-3">
              <h6 className="text-muted mb-2" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                <i className="ni ni-send mr-1" />
                Funding Voucher
              </h6>
               <DocumentSearchList
                 originId={deal?.publicId}
                 originType="voucher"
                 pageSize={5}
                 refreshKey={refreshKey}
                 showDeleteButton={false}
               />
            </div>
          </div>

          {/* Right Column */}
          <div className="col-md-6">
            {/* Blank Check Documents */}
            <div className="mb-3">
              <h6 className="text-muted mb-2" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                <i className="ni ni-money-coins mr-1" />
                Blank Check Documents
              </h6>
               <DocumentSearchList
                 originId={deal?.privateId}
                 originType="blank_check"
                 pageSize={5}
                 refreshKey={refreshKey}
                 showDeleteButton={true}
                 onDeleted={handleDocumentDeleted}
                 onDocumentsLoaded={handleBlankCheckDocumentsLoaded}
               />
            </div>

            {/* Other Documents */}
            <div className="mb-3">
              <h6 className="text-muted mb-2" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                <i className="ni ni-collection mr-1" />
                Other Documents
              </h6>
               <DocumentSearchList
                 originId={deal?.publicId}
                 originType="othersDocuments"
                 pageSize={5}
                 refreshKey={refreshKey}
                 showDeleteButton={true}
               />
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default DocumentsAction;
